import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CreditCard, Plus, ChevronLeft, ChevronRight, CheckCircle, AlertCircle, X,
  IndianRupee, MessageCircle, Download, Eye, EyeOff, Search, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../../context/AuthContext';
import { downloadCSV } from '../../utils/csvExport';
import Modal from '../../components/Modal';
import { useFinancial } from '../../context/FinancialContext';
import FinancialVerifyModal from '../../components/FinancialVerifyModal';

const API = process.env.REACT_APP_BACKEND_URL;
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const PLANS = [
  { months: 1, amount: 2000, label: '1 Month', sublabel: '₹2,000' },
  { months: 4, amount: 7500, label: '4 Months', sublabel: '₹7,500' },
  { months: 6, amount: 11000, label: '6 Months', sublabel: '₹11,000' },
];

function fmt(amount) { return '₹' + amount.toLocaleString('en-IN'); }

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function getWAUrl(student, nextDue) {
  const phone = '91' + student.phone.replace(/\D/g, '');
  const name = student.name;
  let msg;
  if (nextDue) {
    const d = new Date(nextDue + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    msg = `Hi ${name}, your fitness class fee is due on ${d}. Please make the payment at your convenience. Thank you! — Meena's Fitness Forever`;
  } else {
    msg = `Hi ${name}, your fitness class fee payment is pending. Kindly make the payment at your earliest convenience. Thank you! — Meena's Fitness Forever`;
  }
  return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
}

function today() { return new Date().toISOString().split('T')[0]; }

function StatusBadge({ payment }) {
  if (!payment) return (
    <span data-testid="status-pending" className="inline-flex items-center gap-1 bg-red-100 text-red-700 border border-red-200 rounded-full px-3 py-1 text-xs font-heading font-medium">Pending</span>
  );
  const configs = {
    1: { bg: 'bg-green-100 border-green-200 text-green-700', label: 'Paid · 1 Month' },
    4: { bg: 'bg-pink-100 border-pink-200 text-pink-700', label: 'Paid · 4 Months' },
    6: { bg: 'bg-purple-100 border-purple-200 text-purple-700', label: 'Paid · 6 Months' },
    0: { bg: 'bg-blue-100 border-blue-200 text-blue-700', label: 'Paid · Custom' },
  };
  const c = configs[payment.plan_months] ?? configs[0];
  return (
    <span data-testid={`status-paid-${payment.plan_months}`} className={`inline-flex items-center gap-1 ${c.bg} border rounded-full px-3 py-1 text-xs font-heading font-medium`}>
      {c.label}
    </span>
  );
}

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-lg text-sm font-body animate-fade-in-up
      ${type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
      {type === 'success' ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
      {message}
    </div>
  );
}

// ── Searchable Student Selector ───────────────────────────────────────────────
function StudentSearch({ students, value, onChange, onStudentSelect }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = students.find(s => s.id === value);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = students.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) || s.phone.includes(search)
  );

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-3 bg-[#FFFAF0] border-2 border-[#F59E0B]/40 rounded-2xl px-4 py-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-[#FFEDD5] rounded-xl flex items-center justify-center text-[#D97706] font-heading font-semibold text-sm shrink-0">
            {selected.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <p className="font-body text-[#292524] text-sm font-medium">{selected.name}</p>
            <p className="text-xs text-[#A8A29E] font-body">{selected.phone}</p>
          </div>
        </div>
        <button type="button" onClick={() => { onChange(''); setSearch(''); }}
          className="text-[#A8A29E] hover:text-red-500 transition-colors p-1">
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29E]" strokeWidth={1.5} />
        <input
          data-testid="payment-student-search"
          type="text"
          placeholder="Search student by name..."
          value={search}
          onChange={e => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          autoComplete="off"
          className="bg-white border-2 border-[#E7E5E4] rounded-2xl pl-11 pr-4 py-3 focus:ring-4 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body text-sm"
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-[#E7E5E4] rounded-2xl shadow-xl z-20 overflow-hidden max-h-52 overflow-y-auto">
          {filtered.map(s => (
            <button key={s.id} type="button"
              onMouseDown={() => { onStudentSelect(s.id); setSearch(''); setOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#FFFAF0] transition-colors text-left border-b border-[#E7E5E4]/40 last:border-0">
              <div className="w-8 h-8 bg-[#FFEDD5] rounded-xl flex items-center justify-center text-[#D97706] font-heading font-semibold text-xs shrink-0">
                {s.name?.[0]?.toUpperCase()}
              </div>
              <div>
                <p className="font-body text-[#292524] text-sm font-medium">{s.name}</p>
                <p className="font-body text-[#A8A29E] text-xs">{s.phone}</p>
              </div>
            </button>
          ))}
        </div>
      )}
      {open && search && filtered.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-[#E7E5E4] rounded-2xl shadow-xl z-20 px-4 py-3">
          <p className="text-sm text-[#A8A29E] font-body">No students found</p>
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function AdminPayments() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [batchFilter, setBatchFilter] = useState('');
  const [studentNameSearch, setStudentNameSearch] = useState('');
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [preStudent, setPreStudent] = useState(null);
  const [form, setForm] = useState({ student_id: '', plan_months: 1, payment_date: today(), mode: 'cash', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  // Custom payment
  const [customMode, setCustomMode] = useState(false);
  const [customForm, setCustomForm] = useState({ valid_from: today(), valid_to: '', amount: '' });
  // Financial masking
  const { revealed, hide } = useFinancial();
  const [showVerifyModal, setShowVerifyModal] = useState(false);

  // Summary
  const totalCollection = rows.filter(r => r.payment).reduce((s, r) => s + (r.payment?.amount || 0), 0);
  const pendingCount = rows.filter(r => !r.payment && !r.student?.is_archived).length;
  const paidCount = rows.filter(r => r.payment).length;

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/payments?month=${month}&year=${year}`, { headers: getAuthHeaders() });
      setRows(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [month, year]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    axios.get(`${API}/api/admin/students`, { headers: getAuthHeaders() })
      .then(r => setStudents(r.data.filter(s => !s.is_archived)))
      .catch(console.error);
    axios.get(`${API}/api/admin/batches`, { headers: getAuthHeaders() })
      .then(r => setBatches(r.data))
      .catch(console.error);
  }, []);

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };

  // Part 2: Smart default date — use student's next_due if available
  const openModal = (student = null, nextDue = null) => {
    setPreStudent(student);
    const defaultDate = nextDue || today();
    setForm({ student_id: student?.id || '', plan_months: 1, payment_date: defaultDate, mode: 'cash', notes: '' });
    setCustomMode(false);
    setCustomForm({ valid_from: defaultDate, valid_to: '', amount: '' });
    setShowModal(true);
  };

  // When student selected via search: also update default dates from their row
  const handleStudentSelect = (studentId) => {
    const row = rows.find(r => r.student.id === studentId);
    const nextDue = row?.next_due || null;
    const defaultDate = nextDue || today();
    setForm(f => ({ ...f, student_id: studentId, payment_date: defaultDate }));
    setCustomForm(f => ({ ...f, valid_from: defaultDate }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.student_id) { setToast({ message: 'Please select a student.', type: 'error' }); return; }

    if (customMode) {
      // Custom payment validation
      if (!customForm.valid_from || !customForm.valid_to || !customForm.amount) {
        setToast({ message: 'Please fill all custom payment fields.', type: 'error' }); return;
      }
      if (parseFloat(customForm.amount) <= 0) {
        setToast({ message: 'Amount must be greater than zero.', type: 'error' }); return;
      }
      if (customForm.valid_to < customForm.valid_from) {
        setToast({ message: 'Valid To must be on or after Valid From.', type: 'error' }); return;
      }
    }

    setSubmitting(true);
    try {
      const payload = customMode
        ? {
            student_id: form.student_id,
            plan_months: 0,
            payment_date: customForm.valid_from,
            mode: form.mode,
            is_custom: true,
            custom_valid_from: customForm.valid_from,
            custom_valid_to: customForm.valid_to,
            custom_amount: parseInt(customForm.amount, 10),
          }
        : { ...form, plan_months: Number(form.plan_months), is_custom: false };

      await axios.post(`${API}/api/admin/payments`, payload, { headers: getAuthHeaders() });
      const name = students.find(s => s.id === form.student_id)?.name || 'Student';
      setToast({ message: `Payment recorded for ${name}!`, type: 'success' });
      setShowModal(false);
      fetchRows();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Could not save payment.', type: 'error' });
    } finally { setSubmitting(false); }
  };

  // Part 4: Batch filter + student name search applied together with status filter
  const filtered = rows
    .filter(r => {
      if (filter === 'paid') return !!r.payment;
      if (filter === 'pending') return !r.payment;
      return true;
    })
    .filter(r => !batchFilter || r.student.batch_id === batchFilter)
    .filter(r => !studentNameSearch || r.student.name.toLowerCase().includes(studentNameSearch.toLowerCase()));

  const selPlan = PLANS.find(p => p.months === Number(form.plan_months)) || PLANS[0];

  const handleExportPayments = () => {
    const data = rows.map(r => ({
      'Student Name': r.student.name,
      'Phone': r.student.phone,
      'Batch': r.batch_name || 'Unassigned',
      'Payment Date': r.payment?.payment_date || '—',
      'Amount (INR)': r.payment?.amount || '—',
      'Mode': r.payment ? (r.payment.mode === 'upi' ? 'UPI' : 'Cash') : '—',
      'Plan': r.payment ? (r.payment.plan_months === 0 ? 'Custom' : `${r.payment.plan_months} Month${r.payment.plan_months > 1 ? 's' : ''}`) : '—',
      'Status': r.payment ? 'Paid' : 'Pending',
      'Next Due Date': r.next_due || '—',
    }));
    downloadCSV(data, `payments_${MONTHS[month - 1]}_${year}.csv`);
  };

  return (
    <div className="space-y-5 animate-fade-in-up">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {showVerifyModal && <FinancialVerifyModal onClose={() => setShowVerifyModal(false)} />}

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B] font-body mb-1">Manage</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#292524]">Payments</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="export-payments-csv" onClick={handleExportPayments} disabled={rows.length === 0}
            className="flex items-center gap-2 border-2 border-[#E7E5E4] hover:border-[#F59E0B] text-[#57534E] hover:text-[#D97706] rounded-full px-4 py-2.5 text-sm font-heading font-medium transition-all disabled:opacity-40">
            <Download className="w-4 h-4" strokeWidth={1.5} /> Export CSV
          </button>
          <button data-testid="record-payment-button" onClick={() => openModal()}
            className="flex items-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full px-5 py-2.5 text-sm font-heading font-medium shadow-button hover:-translate-y-0.5 transition-all">
            <Plus className="w-4 h-4" strokeWidth={2} /> Record Payment
          </button>
        </div>
      </div>

      {/* Month nav */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-white rounded-2xl p-4 shadow-card border border-[#E7E5E4]/50">
        <div className="flex items-center gap-3">
          <button onClick={prevMonth} data-testid="prev-month" className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-[#FFFAF0] text-[#57534E]">
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <h2 className="font-heading text-base font-semibold text-[#292524] min-w-36 text-center">{MONTHS[month - 1]} {year}</h2>
          <button onClick={nextMonth} data-testid="next-month" className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-[#FFFAF0] text-[#57534E]">
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs font-body bg-green-50 text-green-700 rounded-full px-3 py-1.5 font-medium flex items-center gap-1.5">
            Collected: {revealed ? fmt(totalCollection) : '₹ ••••••'}
            <button data-testid="toggle-collection-visibility-payments"
              onClick={() => revealed ? hide() : setShowVerifyModal(true)}
              className="text-green-600 hover:text-green-800 transition-colors"
              title={revealed ? 'Hide amount' : 'Show amount'}>
              {revealed ? <EyeOff className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />}
            </button>
          </span>
          <span className="text-xs font-body bg-red-50 text-red-700 rounded-full px-3 py-1.5 font-medium">Pending: {pendingCount}</span>
          <span className="text-xs font-body bg-[#FFEDD5] text-[#D97706] rounded-full px-3 py-1.5 font-medium">Paid: {paidCount}</span>
        </div>
      </div>

      {/* Part 4: Filters row — status tabs + batch filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-2 bg-white rounded-2xl p-1.5 shadow-card border border-[#E7E5E4]/50">
          {['all', 'paid', 'pending'].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`px-5 py-2 rounded-xl text-sm font-heading font-medium capitalize transition-all
                ${filter === f
                  ? f === 'pending' ? 'bg-red-500 text-white' : f === 'paid' ? 'bg-green-500 text-white' : 'bg-[#F59E0B] text-white'
                  : 'text-[#57534E] hover:text-[#D97706]'
                }`}>
              {f === 'all' ? `All (${rows.length})` : f === 'paid' ? `Paid (${paidCount})` : `Pending (${pendingCount})`}
            </button>
          ))}
        </div>
        {/* Batch filter */}
        <select value={batchFilter} onChange={e => setBatchFilter(e.target.value)}
          className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-2 focus:border-[#F59E0B] outline-none text-[#292524] font-body text-sm min-w-36">
          <option value="">All Batches</option>
          {batches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        {/* Student name search */}
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29E]" strokeWidth={1.5} />
          <input
            data-testid="payments-student-search"
            type="text"
            placeholder="Search student..."
            value={studentNameSearch}
            onChange={e => setStudentNameSearch(e.target.value)}
            className="bg-white border-2 border-[#E7E5E4] rounded-2xl pl-10 pr-4 py-2 focus:ring-4 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] outline-none text-[#292524] font-body text-sm min-w-44"
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-10 h-10 border-4 border-[#F59E0B] border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-3xl p-10 text-center shadow-card border border-[#E7E5E4]/50">
          <CreditCard className="w-10 h-10 text-[#F59E0B] mx-auto mb-3" strokeWidth={1.5} />
          <p className="font-heading font-medium text-[#292524]">No records for this view</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden lg:block bg-white rounded-3xl shadow-card border border-[#E7E5E4]/50 overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#FFFAF0] border-b border-[#E7E5E4]/60">
                <tr>
                  {['Student', 'Batch', 'Next Due', 'Amount', 'Paid On', 'Status', 'Mode', ''].map(h => (
                    <th key={h} className="text-left text-xs font-semibold text-[#78716C] font-body uppercase tracking-wider px-5 py-3.5">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E7E5E4]/40">
                {filtered.map(row => (
                  <tr key={row.student.id} data-testid={`payment-row-${row.student.id}`} className="hover:bg-[#FFFAF0]/60 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-[#FFEDD5] rounded-xl flex items-center justify-center text-[#D97706] font-heading font-semibold text-xs shrink-0">
                          {row.student.name?.[0]?.toUpperCase()}
                        </div>
                        <span className="font-body text-sm text-[#292524] font-medium">{row.student.name}</span>
                        {row.student.is_archived && <span className="text-xs text-gray-400 font-body">(archived)</span>}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-[#57534E] font-body">{row.batch_name || '—'}</td>
                    <td className="px-5 py-4">
                      {row.next_due ? (
                        <span className={`text-sm font-body font-medium ${new Date(row.next_due) < new Date() ? 'text-red-600' : 'text-[#292524]'}`}>
                          {fmtDate(row.next_due)}
                        </span>
                      ) : <span className="text-sm text-[#A8A29E] font-body">—</span>}
                    </td>
                    <td className="px-5 py-4 text-sm font-body font-medium text-[#292524]">{row.payment ? fmt(row.payment.amount) : '—'}</td>
                    <td className="px-5 py-4 text-sm text-[#57534E] font-body">{row.payment ? fmtDate(row.payment.payment_date) : '—'}</td>
                    <td className="px-5 py-4"><StatusBadge payment={row.payment} /></td>
                    <td className="px-5 py-4">
                      {row.payment ? (
                        <span className={`text-xs font-body capitalize px-2 py-1 rounded-lg ${row.payment.mode === 'upi' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                          {row.payment.mode}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-5 py-4">
                      {!row.student.is_archived && (
                        <div className="flex items-center gap-2">
                          {/* Part 2: pass row.next_due as smart default */}
                          <button data-testid={`record-payment-row-${row.student.id}`}
                            onClick={() => openModal(row.student, row.next_due)}
                            className="text-xs font-body text-[#F59E0B] hover:text-[#D97706] font-medium hover:underline transition-colors whitespace-nowrap">
                            {row.payment ? 'Add Payment' : 'Record →'}
                          </button>
                          {!row.payment && (
                            <a data-testid={`wa-reminder-${row.student.id}`}
                              href={getWAUrl(row.student, row.next_due)} target="_blank" rel="noopener noreferrer"
                              title="Send WhatsApp reminder"
                              className="text-[#25D366] hover:text-[#1ebe5e] transition-colors p-1 rounded-lg hover:bg-green-50">
                              <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
                            </a>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden space-y-3">
            {filtered.map(row => (
              <div key={row.student.id} data-testid={`payment-card-${row.student.id}`}
                className="bg-white rounded-2xl p-4 shadow-card border border-[#E7E5E4]/50">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 bg-[#FFEDD5] rounded-xl flex items-center justify-center text-[#D97706] font-heading font-semibold text-sm shrink-0">
                      {row.student.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-heading font-medium text-[#292524] text-sm">{row.student.name}</p>
                      <p className="text-xs text-[#A8A29E] font-body">{row.batch_name || 'No batch'}</p>
                    </div>
                  </div>
                  <StatusBadge payment={row.payment} />
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs font-body mb-3">
                  <div><span className="text-[#A8A29E]">Amount: </span><span className="text-[#292524] font-medium">{row.payment ? fmt(row.payment.amount) : '—'}</span></div>
                  <div><span className="text-[#A8A29E]">Mode: </span><span className="text-[#292524] capitalize">{row.payment?.mode || '—'}</span></div>
                  <div><span className="text-[#A8A29E]">Paid: </span><span className="text-[#292524]">{row.payment ? fmtDate(row.payment.payment_date) : '—'}</span></div>
                  <div>
                    <span className="text-[#A8A29E]">Next Due: </span>
                    <span className={`${row.next_due && new Date(row.next_due) < new Date() ? 'text-red-600 font-medium' : 'text-[#292524]'}`}>
                      {row.next_due ? fmtDate(row.next_due) : '—'}
                    </span>
                  </div>
                </div>
                {!row.student.is_archived && (
                  <div className="flex gap-2">
                    <button onClick={() => openModal(row.student, row.next_due)}
                      className="flex-1 text-center text-xs font-heading font-medium text-[#F59E0B] hover:text-[#D97706] transition-colors py-1.5 border border-[#F59E0B]/40 rounded-xl hover:bg-[#FFFAF0]">
                      {row.payment ? 'Add Payment' : 'Record Payment'}
                    </button>
                    {!row.payment && (
                      <a data-testid={`wa-reminder-mobile-${row.student.id}`}
                        href={getWAUrl(row.student, row.next_due)} target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-1.5 px-3 py-1.5 border border-green-200 bg-green-50 hover:bg-green-100 text-green-700 rounded-xl text-xs font-heading font-medium transition-colors whitespace-nowrap">
                        <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> Remind
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Record Payment Modal ── */}
      {showModal && (
        <Modal
          title="Record Payment"
          onClose={() => { setShowModal(false); setCustomMode(false); }}
          footer={
            <div className="flex gap-3">
              <button type="button" onClick={() => { setShowModal(false); setCustomMode(false); }}
                className="flex-1 border-2 border-[#E7E5E4] text-[#57534E] rounded-full py-3 font-heading font-medium text-sm hover:border-[#F59E0B] transition-colors">
                Cancel
              </button>
              <button data-testid="submit-payment" type="submit" form="payment-form" disabled={submitting}
                className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full py-3 font-heading font-medium text-sm shadow-button disabled:opacity-60 transition-all">
                {submitting ? 'Saving...' : 'Save Payment'}
              </button>
            </div>
          }
        >
          <form id="payment-form" onSubmit={handleSubmit} className="space-y-4">

            {/* Part 3: Searchable student selector */}
            <div>
              <label className="block text-sm font-medium text-[#57534E] mb-2">Student</label>
              {preStudent ? (
                <div className="flex items-center gap-3 bg-[#FFFAF0] border-2 border-[#E7E5E4] rounded-2xl px-4 py-3">
                  <div className="w-8 h-8 bg-[#FFEDD5] rounded-xl flex items-center justify-center text-[#D97706] font-heading font-semibold text-sm shrink-0">
                    {preStudent.name?.[0]?.toUpperCase()}
                  </div>
                  <span className="font-body text-[#292524] font-medium">{preStudent.name}</span>
                </div>
              ) : (
                <StudentSearch
                  students={students}
                  value={form.student_id}
                  onChange={(id) => setForm(f => ({ ...f, student_id: id }))}
                  onStudentSelect={(id) => handleStudentSelect(id)}
                />
              )}
            </div>

            {/* Part 1: Plan selection OR Custom Payment — mutually exclusive */}
            {!customMode ? (
              <>
                {/* Standard plans */}
                <div>
                  <label className="block text-sm font-medium text-[#57534E] mb-2">Plan</label>
                  <div className="grid grid-cols-3 gap-2">
                    {PLANS.map(plan => (
                      <button key={plan.months} type="button" data-testid={`plan-${plan.months}m`}
                        onClick={() => setForm(f => ({ ...f, plan_months: plan.months }))}
                        className={`rounded-2xl p-3 text-center border-2 transition-all
                          ${Number(form.plan_months) === plan.months
                            ? 'border-[#F59E0B] bg-[#FFFAF0]'
                            : 'border-[#E7E5E4] hover:border-[#F59E0B]/50'
                          }`}>
                        <p className="font-heading font-semibold text-[#292524] text-sm">{plan.label}</p>
                        <p className="text-xs text-[#F59E0B] font-body font-medium mt-0.5">{plan.sublabel}</p>
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-2 bg-[#FFEDD5] rounded-xl px-3 py-2">
                    <IndianRupee className="w-3.5 h-3.5 text-[#D97706]" strokeWidth={2} />
                    <span className="text-sm font-heading font-semibold text-[#D97706]">{selPlan.sublabel}</span>
                    <span className="text-xs text-[#78716C] font-body">— {selPlan.label}</span>
                  </div>
                </div>

                {/* Payment Date + Mode */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-[#57534E] mb-2">Payment Date</label>
                    <input data-testid="payment-date-input" type="date" value={form.payment_date}
                      onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))}
                      className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#57534E] mb-2">Mode</label>
                    <select data-testid="payment-mode-select" value={form.mode}
                      onChange={e => setForm(f => ({ ...f, mode: e.target.value }))}
                      className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body text-sm">
                      <option value="cash">Cash</option>
                      <option value="upi">UPI</option>
                    </select>
                  </div>
                </div>

                {/* Switch to custom */}
                <div className="text-center pt-1">
                  <button type="button" data-testid="switch-to-custom"
                    onClick={() => { setCustomMode(true); setForm(f => ({ ...f, plan_months: 0 })); }}
                    className="text-sm text-[#57534E] hover:text-[#292524] font-body underline-offset-2 hover:underline transition-colors">
                    Record a Custom Payment
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Custom Payment section */}
                <div className="bg-[#FFFAF0] border border-[#F59E0B]/30 rounded-2xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-[#292524] font-heading">Custom Payment</p>
                    <button type="button" onClick={() => { setCustomMode(false); setForm(f => ({ ...f, plan_months: 1 })); }}
                      className="text-xs text-[#F59E0B] hover:text-[#D97706] font-body hover:underline transition-colors">
                      ← Use Standard Plan
                    </button>
                  </div>
                  <p className="text-xs text-[#78716C] font-body leading-relaxed">
                    Use this option when the payment amount or validity period does not match the standard plans.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-[#57534E] mb-1.5">Valid From *</label>
                      <input type="date" value={customForm.valid_from}
                        onChange={e => setCustomForm(f => ({ ...f, valid_from: e.target.value }))}
                        className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-3 py-2.5 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#57534E] mb-1.5">Valid To *</label>
                      <input type="date" value={customForm.valid_to}
                        min={customForm.valid_from}
                        onChange={e => setCustomForm(f => ({ ...f, valid_to: e.target.value }))}
                        className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-3 py-2.5 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#57534E] mb-1.5">Amount (₹) *</label>
                    <input type="number" min="1" placeholder="Enter amount"
                      value={customForm.amount}
                      onChange={e => setCustomForm(f => ({ ...f, amount: e.target.value }))}
                      className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-3 py-2.5 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body text-sm" />
                  </div>
                </div>

                {/* Mode only (no payment date for custom — valid_from serves that) */}
                <div>
                  <label className="block text-sm font-medium text-[#57534E] mb-2">Mode</label>
                  <select value={form.mode} onChange={e => setForm(f => ({ ...f, mode: e.target.value }))}
                    className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body text-sm">
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                  </select>
                </div>
              </>
            )}
          </form>
        </Modal>
      )}
    </div>
  );
}
